import { useState, useCallback, useEffect, createContext, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, AuthState, LoginRequest, RegisterRequest, ApiError } from '@/types';
import { authApi, getToken, clearToken } from '@/lib/api';

interface AuthContextType extends AuthState {
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const navigate = useNavigate();

  // Check for existing token and fetch user on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const user = await authApi.getMe();
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch {
          // Token is invalid or expired
          clearToken();
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.login(data);
      setState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      navigate('/dashboard');
    } catch (error) {
      const apiError = error as ApiError;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: apiError.message || 'Login failed',
      }));
      throw error;
    }
  }, [navigate]);

  const register = useCallback(async (data: RegisterRequest) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.register(data);
      setState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      navigate('/dashboard');
    } catch (error) {
      const apiError = error as ApiError;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: apiError.message || 'Registration failed',
      }));
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      navigate('/');
    }
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    try {
      const user = await authApi.getMe();
      setState((prev) => ({
        ...prev,
        user,
      }));
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 401) {
        clearToken();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        navigate('/');
      }
    }
  }, [navigate]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for checking if user has specific role
export function useHasRole(roles: User['role'][]): boolean {
  const { user } = useAuth();
  return user ? roles.includes(user.role) : false;
}

// Hook for checking if user is parent
export function useIsParentOrAdmin(): boolean {
  return useHasRole(['parent']);
}
